"use client";

import type { CSSProperties } from "react";
import type { LibraryMetric, LibraryProfile } from "./library-data";
import { MetricPanel } from "./MetricPanel";
import { ProfileHeader } from "./ProfileHeader";

export interface CareerHeroHeaderProps {
	readonly profile: LibraryProfile;
	readonly metrics: readonly LibraryMetric[];
	readonly showAllMetrics: boolean;
	readonly onToggleShowAllMetrics: () => void;
	readonly onHeroTap?: (() => void) | undefined;
	readonly onAvatarTap?: (() => void) | undefined;
}

const HERO_MIN_ASPECT_RATIO = 3 / 4;

/**
 * CareerHeroHeader — full-bleed hero background with gradient overlay and the
 * metric panel + profile header stacked on top. Mirrors `CareerHeroHeader`
 * on iOS: hero `scaledToFill` + top→bottom gradient (5%→65% black) + 24pt
 * horizontal padding + min-height enforced at 4:3 of container width.
 */
export function CareerHeroHeader({
	profile,
	metrics,
	showAllMetrics,
	onToggleShowAllMetrics,
	onHeroTap,
	onAvatarTap,
}: Readonly<CareerHeroHeaderProps>) {
	const heroStyle: CSSProperties = {
		position: "relative",
		minHeight: `calc(100vw * ${HERO_MIN_ASPECT_RATIO})`,
		overflow: "hidden",
	};

	const overlayStyle: CSSProperties = {
		position: "absolute",
		inset: 0,
		background:
			"linear-gradient(to bottom, rgb(0 0 0 / 0.05), rgb(0 0 0 / 0.65))",
		pointerEvents: "none",
	};

	const contentStyle: CSSProperties = {
		position: "relative",
		zIndex: 1,
		display: "grid",
		gap: 8,
		padding: "calc(env(safe-area-inset-top, 0px) + 18px) 24px 32px",
	};

	const primaryMetric = metrics[0];
	const additionalMetrics = metrics.slice(1);

	return (
		<section className="library-hero" style={heroStyle}>
			<HeroBackground url={profile.heroImageURL} onTapped={onHeroTap} />
			<div
				className="library-hero__overlay"
				style={overlayStyle}
				aria-hidden="true"
			/>
			<div className="library-hero__content" style={contentStyle}>
				{primaryMetric === undefined ? null : (
					<MetricPanel
						metric={primaryMetric}
						additionalMetrics={additionalMetrics}
						showAllMetrics={showAllMetrics}
						onToggle={onToggleShowAllMetrics}
					/>
				)}
				<ProfileHeader
					profile={profile}
					hideSlogan={showAllMetrics}
					onAvatarTap={onAvatarTap}
				/>
			</div>
		</section>
	);
}

function HeroBackground({
	url,
	onTapped,
}: Readonly<{ url: string | null; onTapped?: (() => void) | undefined }>) {
	const fallbackStyle: CSSProperties = {
		position: "absolute",
		inset: 0,
		background: "linear-gradient(180deg, rgb(41 46 56 / 1), rgb(20 23 26 / 1))",
	};

	if (url === null) {
		return (
			<button
				type="button"
				className="library-hero__bg library-hero__bg--fallback"
				style={fallbackStyle}
				onClick={onTapped}
				aria-label="查看背景图"
				tabIndex={-1}
			/>
		);
	}

	const imgStyle: CSSProperties = {
		position: "absolute",
		inset: 0,
		width: "100%",
		height: "100%",
		objectFit: "cover",
	};

	return (
		<button
			type="button"
			className="library-hero__bg"
			onClick={onTapped}
			aria-label="查看背景图"
			tabIndex={-1}
			style={{
				position: "absolute",
				inset: 0,
				padding: 0,
				border: 0,
				background: "transparent",
			}}
		>
			{/* biome-ignore lint/performance/noImgElement: hero background image rendered eagerly with inline styles; next/image not feasible on static export */}
			<img src={url} alt="" style={imgStyle} loading="eager" />
		</button>
	);
}
